import React, { useState } from 'react';
import MoaniDisplay from './MoaniDisplay';
// import VestingChart from './VestingChart'; // Unused - commented out
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip as MuiTooltip,
  LinearProgress,
  Snackbar
} from '@mui/material';
import {
  Upload as UploadIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  // Delete as DeleteIcon, // Unused - commented out
  TrendingUp as TrendingUpIcon,
  TrendingUp,
  TrendingDown,
  AccountBalanceWallet as WalletIcon,
  AccountBalanceWallet,
  SwapHoriz as TransferIcon,
  Store as MarketplaceIcon,
  Timeline as AnalyticsIcon,
  Add as AddIcon,
  // ArrowUpward as ArrowUpIcon, // Unused - commented out
  // ArrowDownward as ArrowDownIcon, // Unused - commented out
  // CallReceived as IncomingIcon, // Unused - commented out
  // CallMade as OutgoingIcon // Unused - commented out
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  // Cell, // Unused - commented out
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import CSVImportManager from './CSVImportManager';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';
// Vesting calculations now handled server-side only
// import { useWalletVesting } from '../hooks/useWalletVesting';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`allocation-tabpanel-${index}`}
      aria-labelledby={`allocation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function AllocationDashboard() {
  const [tabValue, setTabValue] = useState(0);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [addWalletDialogOpen, setAddWalletDialogOpen] = useState(false);
  const [editWalletDialogOpen, setEditWalletDialogOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [isFullSyncing, _setIsFullSyncing] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncDays, setSyncDays] = useState(7);
  const [isSyncing, setIsSyncing] = useState(false);
  const queryClient = useQueryClient();


  // Fetch dashboard data
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['allocationDashboard'],
    queryFn: async () => {
      const response = await api.get('/allocations/dashboard');
      return response.data;
    }
  });

  // Vesting calculator disabled - tracked wallets moved to separate component
  const _vestingLoading = false; // Unused - placeholder
  const _getWalletVesting = (_walletAddress) => null; // Unused - placeholder


  // Wallet vesting data comes from server-side calculation


  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast(prev => ({ ...prev, open: false }));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };






  const handleAddToTracked = (address) => {
    setSelectedAddress(address);
    setAddWalletDialogOpen(true);
  };

  const handleSyncLastDays = async () => {
    try {
      setIsSyncing(true);
      setSyncDialogOpen(false);
      
      // Calculate start date
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - syncDays);
      
      showToast(`Starting sync for last ${syncDays} days...`, 'info');
      
      const response = await api.post('/sync-debug/force-sync', {
        startDate: startDate.toISOString()
      });
      
      if (response.data.success) {
        showToast(`Sync started for last ${syncDays} days. Processing ${response.data.blockRange?.total || 'unknown'} blocks.`, 'success');
        
        // Refresh dashboard data after a delay
        setTimeout(() => {
          queryClient.invalidateQueries(['allocationDashboard']);
        }, 5000);
      } else {
        showToast('Failed to start sync', 'error');
      }
    } catch (error) {
      console.error('Sync error:', error);
      showToast(error.response?.data?.error || 'Failed to start sync', 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const addToTrackedMutation = useMutation({
    mutationFn: async (walletData) => {
      const response = await api.post('/allocations/wallets/add', walletData);
      return response.data;
    },
    onSuccess: () => {
        showToast('Wallet added to tracked list successfully', 'success');
      setAddWalletDialogOpen(false);
      setSelectedAddress('');
      queryClient.invalidateQueries(['trackedWallets']);
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to add wallet', 'error');
    }
  });

  const updateWalletMutation = useMutation({
    mutationFn: async ({ walletId, updates }) => {
      const response = await api.put(`/allocations/wallets/${walletId}`, updates);
      return response.data;
    },
    onSuccess: () => {
      showToast('Wallet updated successfully', 'success');
      setEditWalletDialogOpen(false);
      setSelectedWallet(null);
      queryClient.invalidateQueries(['trackedWallets']);
      queryClient.invalidateQueries(['allocationDashboard']);
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to update wallet', 'error');
    }
  });

  const deleteWalletMutation = useMutation({
    mutationFn: async (walletId) => {
      const response = await api.delete(`/allocations/wallets/${walletId}`);
      return response.data;
    },
    onSuccess: () => {
      showToast('Wallet deleted successfully', 'success');
      queryClient.invalidateQueries(['trackedWallets']);
      queryClient.invalidateQueries(['allocationDashboard']);
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to delete wallet', 'error');
    }
  });

  const confirmAddWallet = () => {
    if (!selectedAddress) return;
    
    addToTrackedMutation.mutate({
      walletAddress: selectedAddress,
      name: `Tracked Wallet ${selectedAddress.slice(0, 8)}`,
      notes: 'Added from transaction analysis',
      vestingType: 'Unknown'
    });
  };

  const _handleEditWallet = (_wallet) => {
    // Unused - wallet editing moved to TrackedWallets component
    // setSelectedWallet(wallet);
    // setEditWalletDialogOpen(true);
  };

  const _handleDeleteWallet = (_walletId) => {
    // Unused - wallet deletion moved to TrackedWallets component
    // if (window.confirm('Are you sure you want to delete this tracked wallet? This will remove all associated data.')) {
    //   deleteWalletMutation.mutate(walletId);
    // }
  };

  const _handleForceResyncWallet = async (_wallet) => {
    if (window.confirm(`Are you sure you want to force resync ${wallet.name || wallet.walletAddress}? This will scan for missing transactions from August 25, 2025 onwards (existing data will be preserved).`)) {
      try {
        showToast(`Starting comprehensive resync for ${formatAddress(wallet.walletAddress)}...`, 'info');
        
        const response = await api.post(`/allocations/wallets/${wallet.id}/force-resync`);
        showToast(response.data.message, 'success');
        
        // Refresh wallet data
        queryClient.invalidateQueries(['trackedWallets']);
        queryClient.invalidateQueries(['allocationDashboard']);
        
      } catch (error) {
        console.error('Error starting force resync:', error);
        showToast(error.response?.data?.error || 'Failed to start force resync', 'error');
      }
    }
  };

  const handleForceResyncAll = async () => {
    if (window.confirm('Are you sure you want to force resync ALL tracked wallets? This will scan for missing transactions from August 25, 2025 onwards (existing data preserved). This process may take a very long time.')) {
      try {
        showToast('Starting comprehensive resync for all tracked wallets...', 'info');
        
        const response = await api.post('/allocations/wallets/force-resync-all');
        showToast(response.data.message, 'success');
        
        // Refresh data
        queryClient.invalidateQueries(['trackedWallets']);
        queryClient.invalidateQueries(['allocationDashboard']);
        
      } catch (error) {
        console.error('Error starting force resync all:', error);
        showToast(error.response?.data?.error || 'Failed to start force resync', 'error');
      }
    }
  };




  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getAllocationProgress = () => {
    // If viewing a specific wallet, use aggregated vesting calculation
    if (selectedWallet) {
      const totalSentOut = parseFloat(selectedWallet.totalSentOut || 0);
      const totalReceivedExcludingVesting = parseFloat(selectedWallet.totalReceivedExcludingVesting || 0);
      const currentBalance = parseFloat(selectedWallet.moaniBalance || 0);
      
      // Use wallet data from server
      const totalAllocation = parseFloat(selectedWallet.totalAllocation || 0);
      const totalVestedAmount = parseFloat(selectedWallet.totalVestedAmount || 0);
      const totalUnvestedAmount = totalAllocation - totalVestedAmount;
      const vestedPercentage = totalAllocation > 0 ? (totalVestedAmount / totalAllocation) * 100 : 0;
      
      // Calculate compliance: how much of vested amount is still held
      const netOutgoing = totalSentOut - totalReceivedExcludingVesting;
      const shouldBeHolding = Math.max(0, totalVestedAmount - netOutgoing);
      const compliancePercentage = totalVestedAmount > 0 ? (shouldBeHolding / totalVestedAmount) * 100 : 100;
      
      // Calculate allocation breakdown from wallet data
      const allocationBreakdown = selectedWallet.allocations || [];
      
      console.log('💰 Wallet Aggregated Vesting Progress:', {
        walletAddress: selectedWallet.walletAddress,
        totalAllocation,
        totalVestedAmount,
        totalUnvestedAmount,
        vestedPercentage,
        totalSentOut,
        shouldBeHolding,
        compliancePercentage,
        currentBalance,
        allocationsCount: allocationBreakdown.length
      });
      
      if (totalAllocation === 0) return { 
        percentage: 0, 
        spentPercentage: 0,
        color: 'grey', 
        status: 'no-data',
        totalAllocation: 0,
        totalSentOut: 0,
        totalHeld: shouldBeHolding,
        currentBalance,
        isUsingRealData: false,
        vestingInfo: null
      };
      
      let color = 'success';
      let status = 'holding';
      
      if (compliancePercentage < 25) {
        color = 'error';
        status = 'low-holding';
      } else if (compliancePercentage < 50) {
        color = 'warning';
        status = 'concerning';
      } else if (compliancePercentage < 75) {
        color = 'info';
        status = 'moderate';
      }
      
      return {
        percentage: Math.max(0, compliancePercentage),
        spentPercentage: Math.max(0, 100 - compliancePercentage),
        color,
        status,
        totalAllocation,
        totalSentOut,
        totalHeld: shouldBeHolding,
        currentBalance,
        isUsingRealData: true,
        vestingInfo: {
          vestedAmount: totalVestedAmount,
          unvestedAmount: totalUnvestedAmount,
          vestedPercentage,
          nextUnlockDate: selectedWallet.nextUnlockDate || null,
          nextUnlockAmount: selectedWallet.nextUnlockAmount || null,
          isFullyVested: selectedWallet.isFullyVested || false
        },
        vestedAmount: totalVestedAmount,
        unvestedAmount: totalUnvestedAmount,
        vestedPercentage,
        allocationBreakdown
      };
    }
    
    // Default dashboard view
    if (!dashboardData) return { 
      percentage: 0, 
      spentPercentage: 0,
      color: 'success', 
      status: 'loading',
      totalAllocation: 0,
      totalSentOut: 0,
      totalHeld: 0
    };
    
    console.log('📊 Dashboard data received:', dashboardData);
    
    const totalAllocation = parseFloat(dashboardData.totalAllocation || 0);
    const totalSentOut = parseFloat(dashboardData.totalSentOut || 0);
    const totalReceived = parseFloat(dashboardData.totalReceived || 0);
    
    // Vesting compliance: allocation - sent + received
    const totalVestingBalance = Math.max(0, totalAllocation - totalSentOut + totalReceived);
    const isUsingRealData = totalSentOut > 0 || totalReceived > 0;
    
    console.log('💰 MOANI Progress calculation:', {
      totalAllocation,
      totalSentOut,
      totalReceived,
      vestingBalance: totalVestingBalance,
      usingRealData: isUsingRealData
    });
    
    if (totalAllocation === 0) return { 
      percentage: 0, 
      spentPercentage: 0,
      color: 'grey', 
      status: 'no-data',
      totalAllocation: 0,
      totalSentOut: 0,
      totalHeld: 0,
      isUsingRealData: false
    };
    
    // Use vested amount from dashboard if available, otherwise fall back to total allocation
    const vestedAmount = parseFloat(dashboardData.totalVestedAmount || 0);
    const maxAmount = vestedAmount > 0 ? vestedAmount : totalAllocation;
    const heldPercentage = maxAmount > 0 ? (totalVestingBalance / maxAmount) * 100 : 0;
    const spentPercentage = 100 - heldPercentage;
    
    let color = 'success'; // Green - most tokens held
    let status = 'excellent';
    
    if (spentPercentage > 75) {
      color = 'error'; // Red - most tokens spent/sold
      status = 'critical';
    } else if (spentPercentage > 50) {
      color = 'warning'; // Orange - significant amount spent
      status = 'concerning';
    } else if (spentPercentage > 25) {
      color = 'info'; // Blue - moderate spending
      status = 'moderate';
    }
    
    return {
      percentage: Math.max(0, heldPercentage),
      spentPercentage: Math.max(0, spentPercentage),
      color,
      status,
      totalAllocation,
      totalSentOut,
      totalHeld: totalVestingBalance,
      totalReceived,
      isUsingRealData,
      vestedAmount: vestedAmount > 0 ? vestedAmount : totalAllocation,
      vestedPercentage: dashboardData.vestedPercentage || 100
    };
  };

  if (dashboardLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Allocation Dashboard
        </Typography>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setTabValue(2)}
        >
          Import CSV
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WalletIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Total Wallets</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {formatNumber(dashboardData?.walletStats?.totalWallets)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dashboardData?.walletStats?.activeWallets} active
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TransferIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">Transactions</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {formatNumber(dashboardData?.transactionStats?.totalTransactions)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last 30 days
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MarketplaceIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">Exchange Transfers</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {formatNumber(dashboardData?.transactionStats?.exchangeTransfers)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                To known exchanges
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6">First Transfers</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {formatNumber(dashboardData?.transactionStats?.firstTransfers)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Initial movements
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="allocation dashboard tabs">
          <Tab label="Overview" />
          <Tab label="CSV Import" />
          <Tab label="Recent Activity" />
          <Tab label="Analytics" />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Dashboard Overview
              </Typography>
          <Typography variant="body2" color="text.secondary">
                Welcome to the Allocation Dashboard. Monitor wallet allocations, track transactions, and sync blockchain data.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                startIcon={<SyncIcon />}
                onClick={() => setSyncDialogOpen(true)}
                disabled={isSyncing || isFullSyncing}
                sx={{ minWidth: 180 }}
              >
                Sync Last {syncDays} Days
              </Button>
              <Button
                variant="outlined"
                color="warning"
                size="large"
                startIcon={<RefreshIcon />}
                onClick={handleForceResyncAll}
                disabled={isFullSyncing || isSyncing}
                sx={{ minWidth: 180 }}
              >
                Force Resync All
              </Button>
            </Box>
          </Box>

          {dashboardData && (
            <Grid container spacing={3}>
              {/* Single MOANI Allocation Progress Bar - Full Width */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    {(() => {
                      const progress = getAllocationProgress();
                      return (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Box>
                              <Typography variant="h5" color="primary">
                                MOANI Token Allocation Overview
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Chip 
                                  label={progress.status.toUpperCase()} 
                                  color={progress.color}
                                  variant="filled"
                                  size="small"
                                />
                                <Chip 
                                  label={progress.isUsingRealData ? "REAL BLOCKCHAIN DATA" : "CALCULATED DATA"} 
                                  color={progress.isUsingRealData ? "success" : "warning"}
                                  variant="outlined"
                                  size="small"
                                />
                              </Box>
                            </Box>
                          </Box>
                          
                          {/* Large Progress Bar */}
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body1" color="text.primary">
                                <strong>{formatNumber(progress.currentBalance || progress.totalHeld)} MOANI</strong> Currently Held
                              </Typography>
                              <Typography variant="body1" color="text.secondary">
                                of <strong>{formatNumber(progress.vestedAmount || progress.totalAllocation)} MOANI</strong> Vested
                              </Typography>
                            </Box>
                            
                            
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(100, progress.percentage)} 
                              sx={{ 
                                height: 20, 
                                borderRadius: 10,
                                backgroundColor: '#f5f5f5',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: progress.percentage > 100 ? '#ffd700' : // Gold for over 100%
                                                 progress.color === 'error' ? '#f44336' : 
                                                 progress.color === 'warning' ? '#ff9800' :
                                                 progress.color === 'info' ? '#2196f3' : '#66bb6a',
                                  borderRadius: 10
                                }
                              }} 
                            />
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                              <Typography variant="body2" color={progress.percentage > 100 ? '#ffd700' : 'success.main'}>
                                <strong>{progress.percentage.toFixed(1)}%</strong> of Vested Amount Held
                              </Typography>
                              {progress.percentage > 100 && (
                                <Typography variant="body2" color="#ffd700" sx={{ fontWeight: 'bold' }}>
                                  OVER-HOLDING
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Summary Stats */}
                          <Grid container spacing={2}>
                            <Grid item xs={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" color="primary">
                                  {formatNumber(dashboardData.totalWallets || 0)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Tracked Wallets
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" color="success.main">
                                  {formatNumber(progress.totalHeld)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  MOANI Held
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" color="error.main">
                                  {formatNumber(progress.totalSentOut)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  MOANI Sent
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" color="warning.main">
                                  {formatNumber(dashboardData.walletsNeedingSync || 0)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Need Sync
          </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Allocation Summary Overview */}
          {dashboardData && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#00e676', mb: 3 }}>
                💰 Allocation Summary Overview
              </Typography>
              
              <Grid container spacing={3}>
                {/* Total Vested Amount */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    color: 'white',
                    height: '100%'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                          borderRadius: '50%', 
                          p: 1.5, 
                          mr: 2 
                        }}>
                          <TrendingUp sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Total Vested
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            Available for claiming
                          </Typography>
                        </Box>
                      </Box>
                      <MoaniDisplay 
                        amount={dashboardData.totalVestedAmount || 0} 
                        isWei={false}
                        variant="h4" 
                        sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}
                        showTooltip={true}
                      />
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {dashboardData.vestedPercentage?.toFixed(1)}% of total allocation
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Currently Held (Claimed) */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)',
                    color: 'white',
                    height: '100%'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                          borderRadius: '50%', 
                          p: 1.5, 
                          mr: 2 
                        }}>
                          <AccountBalanceWallet sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Currently Held
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            In tracked wallets
                          </Typography>
                        </Box>
                      </Box>
                      <MoaniDisplay 
                        amount={dashboardData.totalRealMoaniHeld || 0} 
                        isWei={false}
                        variant="h4" 
                        sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}
                        showTooltip={true}
                      />
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {dashboardData.totalVestedAmount > 0 
                          ? ((dashboardData.totalRealMoaniHeld / dashboardData.totalVestedAmount) * 100).toFixed(1)
                          : 0}% of vested amount
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Total Sent Out */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
                    color: 'white',
                    height: '100%'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                          borderRadius: '50%', 
                          p: 1.5, 
                          mr: 2 
                        }}>
                          <TrendingDown sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Total Sent Out
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            Transferred/sold
                          </Typography>
                        </Box>
                      </Box>
                      <MoaniDisplay 
                        amount={dashboardData.totalSentOut || 0} 
                        isWei={false}
                        variant="h4" 
                        sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}
                        showTooltip={true}
                      />
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {dashboardData.totalVestedAmount > 0 
                          ? ((dashboardData.totalSentOut / dashboardData.totalVestedAmount) * 100).toFixed(1)
                          : 0}% of vested amount
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Summary Statistics */}
                <Grid item xs={12}>
                  <Card sx={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 3 }}>
                        📊 Allocation Flow Summary
                      </Typography>
                      
                      <Grid container spacing={4}>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Total Allocated
                            </Typography>
                            <MoaniDisplay 
                              amount={dashboardData.totalAllocation || 0} 
                              isWei={false}
                              variant="h6" 
                              color="primary"
                            />
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Still Unvested
                            </Typography>
                            <MoaniDisplay 
                              amount={dashboardData.totalUnvestedAmount || 0} 
                              isWei={false}
                              variant="h6" 
                              color="warning.main"
                            />
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Retention Rate
                            </Typography>
                            <Typography variant="h6" color="success.main">
                              {dashboardData.totalVestedAmount > 0 
                                ? (((dashboardData.totalRealMoaniHeld || 0) / dashboardData.totalVestedAmount) * 100).toFixed(1)
                                : 0}%
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Transfer Rate
                            </Typography>
                            <Typography variant="h6" color="error.main">
                              {dashboardData.totalVestedAmount > 0 
                                ? (((dashboardData.totalSentOut || 0) / dashboardData.totalVestedAmount) * 100).toFixed(1)
                                : 0}%
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Analytics Charts */}
          {dashboardData?.timeSeriesData && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#00e676', mb: 3 }}>
                  📈 Transaction Analytics
                </Typography>
                
                <Grid container spacing={3}>
                  {/* Daily Volume Chart */}
                  <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 3, backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                      <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
                        Daily Transaction Volume (30 Days)
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={dashboardData.timeSeriesData.daily}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis dataKey="date" stroke="#fff" />
                          <YAxis stroke="#fff" />
                          <MuiTooltip 
                            contentStyle={{ backgroundColor: '#2a2a2a', border: '1px solid #444', color: '#fff' }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="totalVolume" stroke="#00e676" strokeWidth={2} name="Volume (MOANI)" />
                          <Line type="monotone" dataKey="transactionCount" stroke="#2196f3" strokeWidth={2} name="Transaction Count" />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  </Grid>

                  {/* Decile Distribution Chart */}
                  <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 3, backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                      <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
                        Wallet Distribution by % Sold
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dashboardData.decileDistribution}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis dataKey="decile" stroke="#fff" angle={-45} textAnchor="end" height={80} />
                          <YAxis stroke="#fff" />
                          <MuiTooltip 
                            contentStyle={{ backgroundColor: '#2a2a2a', border: '1px solid #444', color: '#fff' }}
                          />
                          <Bar dataKey="walletCount" fill="#00e676" name="Wallet Count" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Grid>

                  {/* Holding vs Selling Pie Chart */}
                  <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 3, backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                      <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
                        Holders vs Sellers
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Holding All', value: dashboardData.holdingAnalytics?.walletsHoldingAll || 0, fill: '#00e676' },
                              { name: 'Sold Some', value: dashboardData.holdingAnalytics?.walletsWithSales || 0, fill: '#f44336' }
                            ]}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                          />
                          <MuiTooltip 
                            contentStyle={{ backgroundColor: '#2a2a2a', border: '1px solid #444', color: '#fff' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card>
                  </Grid>

                  {/* Weekly Trends Chart */}
                  <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 3, backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                      <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
                        Weekly Transaction Trends (12 Weeks)
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={dashboardData.timeSeriesData.weekly}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis dataKey="week" stroke="#fff" />
                          <YAxis stroke="#fff" />
                          <MuiTooltip 
                            contentStyle={{ backgroundColor: '#2a2a2a', border: '1px solid #444', color: '#fff' }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="totalVolume" stroke="#00e676" strokeWidth={2} name="Volume" />
                          <Line type="monotone" dataKey="activeWallets" stroke="#ff9800" strokeWidth={2} name="Active Wallets" />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
        </TabPanel>


        {/* CSV Import Tab */}
        <TabPanel value={tabValue} index={1}>
          <CSVImportManager />
        </TabPanel>

        {/* Recent Activity Tab */}
        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                            <TableCell>From Wallet</TableCell>
                            <TableCell>To Address</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Flags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData?.recentActivity?.map((activity, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {new Date(activity.transactionTimestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {activity.walletName ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                            {activity.walletName}
                          </Typography>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                            {formatAddress(activity.fromAddress)}
                          </Typography>
                          <MuiTooltip title="View wallet details (moved to TrackedWallets component)">
                            <IconButton 
                              size="small"
                              onClick={() => {
                                // Wallet details moved to TrackedWallets component
                                // Navigate to tracked wallets page if needed
                                showToast('Wallet details available in TrackedWallets component', 'info');
                                // Unused wallet variable removed - functionality moved
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </MuiTooltip>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {formatAddress(activity.fromAddress)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {formatAddress(activity.toAddress)}
                      </Typography>
                        <MuiTooltip title="Add to tracked wallets">
                          <IconButton 
                            size="small"
                            onClick={() => handleAddToTracked(activity.toAddress)}
                            sx={{ p: 0.5 }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </MuiTooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={activity.txType} size="small" />
                    </TableCell>
                    <TableCell>{activity.platform || '-'}</TableCell>
                    <TableCell>
                      {activity.isToKnownExchange && (
                        <Chip label="Exchange" size="small" color="warning" sx={{ mr: 0.5 }} />
                      )}
                      {activity.isToKnownMarketplace && (
                        <Chip label="Marketplace" size="small" color="info" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AnalyticsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Analytics charts coming soon...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This will include bubble maps, transaction flow analysis, and detailed metrics.
            </Typography>
          </Box>
        </TabPanel>

      </Paper>

      {/* Add Wallet to Tracked List Dialog */}
      <Dialog open={addWalletDialogOpen} onClose={() => setAddWalletDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Wallet to Tracked List</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Add this wallet address to the tracked wallets list for monitoring:
          </Typography>
          
          <TextField
            fullWidth
            label="Wallet Address"
            value={selectedAddress}
            disabled
            sx={{ mb: 2, fontFamily: 'monospace' }}
          />
          
          <Alert severity="info" sx={{ mb: 2 }}>
            This wallet will be added with default settings:
            <ul>
              <li><strong>Name:</strong> Tracked Wallet {selectedAddress.slice(0, 8)}</li>
              <li><strong>Notes:</strong> Added from transaction analysis</li>
              <li><strong>Vesting Type:</strong> Unknown</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddWalletDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmAddWallet} 
            variant="contained"
            disabled={addToTrackedMutation.isPending}
            startIcon={<AddIcon />}
          >
            {addToTrackedMutation.isPending ? 'Adding...' : 'Add to Tracked'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Wallet Dialog */}
      <Dialog open={editWalletDialogOpen} onClose={() => setEditWalletDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Tracked Wallet</DialogTitle>
        <DialogContent>
          {selectedWallet && (
            <EditWalletForm 
              wallet={selectedWallet}
              onSave={(updates) => {
                updateWalletMutation.mutate({ walletId: selectedWallet.id, updates });
              }}
              onCancel={() => setEditWalletDialogOpen(false)}
              isLoading={updateWalletMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Sync Last X Days Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Sync Recent Transactions
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Sync blockchain transactions from the last X days. This will fetch any missing transactions 
              that may have occurred during downtime or sync failures.
            </Typography>
            
            <TextField
              fullWidth
              label="Number of Days"
              type="number"
              value={syncDays}
              onChange={(e) => setSyncDays(Math.max(1, Math.min(30, parseInt(e.target.value) || 7)))}
              inputProps={{ min: 1, max: 30 }}
              helperText="Enter number of days to sync back (1-30 days)"
              sx={{ mb: 2 }}
            />
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This will sync transactions from <strong>{new Date(Date.now() - syncDays * 24 * 60 * 60 * 1000).toLocaleDateString()}</strong> to today.
                Existing transactions will not be duplicated.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSyncLastDays}
            variant="contained"
            startIcon={<SyncIcon />}
            disabled={isSyncing}
          >
            {isSyncing ? 'Starting Sync...' : `Sync Last ${syncDays} Days`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseToast} 
          severity={toast.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}

// Edit Wallet Form Component
function EditWalletForm({ wallet, onSave, onCancel, isLoading }) {
  const [formData, setFormData] = useState({
    walletAddress: wallet.walletAddress || '',
    name: wallet.name || '',
    email: wallet.email || '',
    allocation: wallet.allocation || '',
    vestingType: wallet.vestingType || 'Unknown',
    pricePerToken: wallet.pricePerToken || '',
    totalPrice: wallet.totalPrice || '',
    notes: wallet.notes || '',
    csvBatch: wallet.csvBatch || ''
  });

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = () => {
    // Only send changed fields
    const updates = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] !== (wallet[key] || '')) {
        updates[key] = formData[key];
      }
    });

    if (Object.keys(updates).length === 0) {
      onCancel();
      return;
    }

    onSave(updates);
  };

  return (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Wallet Address"
            value={formData.walletAddress}
            onChange={handleChange('walletAddress')}
            placeholder="0x..."
            sx={{ fontFamily: 'monospace' }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={handleChange('name')}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            value={formData.email}
            onChange={handleChange('email')}
            type="email"
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Allocation"
            value={formData.allocation}
            onChange={handleChange('allocation')}
            type="number"
            inputProps={{ step: "0.00000001" }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Vesting Type</InputLabel>
            <Select
              value={formData.vestingType}
              onChange={handleChange('vestingType')}
              label="Vesting Type"
            >
              <MenuItem value="Unknown">Unknown</MenuItem>
              <MenuItem value="Investor">Investor</MenuItem>
              <MenuItem value="Team">Team</MenuItem>
              <MenuItem value="Advisor">Advisor</MenuItem>
              <MenuItem value="Community">Community</MenuItem>
              <MenuItem value="Marketing">Marketing</MenuItem>
              <MenuItem value="Development">Development</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Price Per Token"
            value={formData.pricePerToken}
            onChange={handleChange('pricePerToken')}
            type="number"
            inputProps={{ step: "0.00000001" }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Total Price"
            value={formData.totalPrice}
            onChange={handleChange('totalPrice')}
            type="number"
            inputProps={{ step: "0.00000001" }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="CSV Batch"
            value={formData.csvBatch}
            onChange={handleChange('csvBatch')}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            value={formData.notes}
            onChange={handleChange('notes')}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : <EditIcon />}
        >
          {isLoading ? 'Updating...' : 'Update Wallet'}
        </Button>
      </Box>
    </Box>
  );
}

export default AllocationDashboard;

